import React from 'react';
import type { AnchorHTMLAttributes, ComponentProps } from 'react';
import styled from '@emotion/styled';
import { backgroundColor, fontSize, lineHeight, right, top as topProperty } from 'styled-system';
import type {
  BackgroundColorProps,
  FontSizeProps,
  LineHeightProps,
  RightProps,
  TopProps,
} from 'styled-system';

import BaseRoundedButton from 'common/components/Button/RoundedButton';
import DrawerTrigger from 'common/components/DrawerTrigger';
import { Flex } from 'common/components/Flex';
import type { FlexProps } from 'common/components/Flex';
import { BodyRegularBR2 } from 'common/components/Typography';
import { usePromoEvents } from 'common/hooks/dynamic-yield';
import { useLocalization } from 'common/providers/LocaleProvider';
import { dispatchTrackingEventGA4 } from 'common/tracking/dispatchTrackingEventGA4';
import { BannerVoucherV2Placement, Element, TrackingEventName } from 'common/types/tracking';
import type { BannerVoucherV2Props } from '../BannerVoucherV2';
import CampaignSection from './CampaignSection';
import Container from './Container';
import VoucherCode from './VoucherCode';

type StyledRoundedButton<T = object> = AnchorHTMLAttributes<HTMLAnchorElement> & T;

export type InfoDrawerContentProps = Pick<
  BannerVoucherV2Props,
  | 'shortName'
  | 'voucherCode'
  | 'onlyHomeClub'
  | 'isHomeClubMember'
  | 'countdownEndDate'
  | 'campaignTextColor'
  | 'campaignBackgroundColor'
  | 'legalDisclaimerContent'
  | 'extraDisclaimerContent'
  | 'name'
> &
  Partial<
    Pick<BannerVoucherV2Props, 'dyDecisionId' | 'dyVariationId' | 'visitorHash' | 'placement'>
  >;

type InfoDrawerProps = TopProps & InfoDrawerContentProps;

const INFO_ICON_SIZE = 32;

const InfoButtonContainer = styled(Flex)<FlexProps & RightProps & TopProps>`
  ${topProperty};
  ${right};
`;

const infoButtonContainerStyles: Partial<ComponentProps<typeof InfoButtonContainer>> = {
  position: 'absolute',
  right: '8px',
};

const RoundedButton = styled(BaseRoundedButton)<
  StyledRoundedButton<FontSizeProps & LineHeightProps & BackgroundColorProps>
>`
  ${fontSize};
  ${lineHeight};
  ${backgroundColor};
`;

const secondaryBtnStyles: ComponentProps<typeof RoundedButton> = {
  as: 'a',
  kind: 'secondary',
  bg: 'sys.neutral.background.default',
};

export const InfoDrawerContent = ({
  shortName,
  voucherCode,
  onlyHomeClub,
  isHomeClubMember,
  countdownEndDate,
  campaignTextColor,
  campaignBackgroundColor,
  legalDisclaimerContent,
  extraDisclaimerContent,
  placement,
  ...dyParams
}: InfoDrawerProps) => {
  const { t } = useLocalization();

  const { onPromoClick } = usePromoEvents({
    ...dyParams,
    placement: placement ?? BannerVoucherV2Placement.BannerVoucherPdpDrawer,
  });

  return (
    <Container p="sp_16" gap="sp_12" flexDirection="column" color={campaignTextColor}>
      {shortName && (
        <Flex p="sp_12" flexDirection="column" borderRadius="medium" bg={campaignBackgroundColor}>
          <Flex flexDirection="column" gap="sp_12">
            <CampaignSection isHeadline shortName={shortName} countdownEndDate={countdownEndDate} />
            <VoucherCode voucherCode={voucherCode} onClick={onPromoClick} />
            {onlyHomeClub ? (
              isHomeClubMember ? (
                <BodyRegularBR2>{t('h24_banner_voucher_drawer_hc_member_label')}</BodyRegularBR2>
              ) : (
                <>
                  <BodyRegularBR2>{t('h24_banner_voucher_drawer_hc_label')}</BodyRegularBR2>
                  <RoundedButton
                    {...secondaryBtnStyles}
                    href={t('h24_banner_voucher_drawer_hc_cta_href')}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('h24_hc_submit_btn_label')}
                  </RoundedButton>
                </>
              )
            ) : null}
          </Flex>
        </Flex>
      )}
      {extraDisclaimerContent && (
        <BodyRegularBR2
          color="sys.neutral.text.strong"
          dangerouslySetInnerHTML={{ __html: extraDisclaimerContent }}
        />
      )}
      {legalDisclaimerContent && (
        <BodyRegularBR2 color="sys.neutral.text.strong">{legalDisclaimerContent}</BodyRegularBR2>
      )}
    </Container>
  );
};

const InfoDrawer = ({ top, ...props }: InfoDrawerProps) => {
  const { t } = useLocalization();

  if (!props.legalDisclaimerContent && !props.extraDisclaimerContent) return null;

  const onClickHandler = () => {
    dispatchTrackingEventGA4({
      event: TrackingEventName.BannerVoucherClick,
      element: Element.Button,
      label: 'disclaimer-link',
    });
  };

  return (
    <InfoButtonContainer {...infoButtonContainerStyles} top={top}>
      <DrawerTrigger
        key="info"
        title={t('h24_important_info')}
        ariaLabel={t('h24_banner_voucher_v2_arial_label')}
        content={<InfoDrawerContent {...props} />}
        size={INFO_ICON_SIZE}
        fill={props.campaignTextColor}
        onTrackingClick={onClickHandler}
      />
    </InfoButtonContainer>
  );
};

export default InfoDrawer;
